// Shared toast queue.
//
// Before A-5 the `useToast()` hook held a per-component `useState`, which
// meant every feature-side `show()` pushed into a queue that no rendered
// <ToastContainer> ever subscribed to — the root <GlobalToastContainer>
// had its own local queue and never received those toasts.
//
// This Zustand store is the single source of truth for toasts. The hook
// in `components/ui/Toast.jsx` is a thin proxy onto it so the existing
// `{ toasts, show, remove, addToast }` surface used by ~40 callers keeps
// compiling unchanged.
//
// Each toast self-dismisses inside the <Toast> component via its own
// timer (see `TOAST_DURATION` in `components/ui/Toast.jsx`); the store
// just owns the queue. Click-dismiss is handled by the toast's close
// button calling `dismiss(id)` through the hook.

import { create } from 'zustand'

// Hard cap to prevent runaway stacking. Matches the prior local-state
// implementation: when the queue grows past this, the oldest toasts are
// dropped from the front.
const MAX_TOASTS = 5

// Cheap id generator. Avoids depending on `crypto.randomUUID` (which is
// missing in older JSDOM and some embedded WebViews) while staying
// collision-resistant for the lifetime of a single page: timestamp +
// monotonically-increasing counter + 36-bit random suffix.
let counter = 0
function nextId() {
  counter = (counter + 1) >>> 0
  return `t_${Date.now()}_${counter}_${Math.random().toString(36).slice(2, 10)}`
}

/** Toast severity. Drives the rendered colour/icon in <Toast>. */
export type ToastType = 'success' | 'error' | 'warning' | 'info'

/** Optional CTA rendered alongside a toast. */
export interface ToastAction {
  label: string
  onClick: () => void
}

/** Options accepted by `show()`. */
export interface ToastOptions {
  action?: ToastAction
}

/** A single queued toast. */
export interface Toast {
  id: string
  message: string
  type: ToastType
  action?: ToastAction
}

export interface ToastState {
  toasts: Toast[]
  /**
   * Append a toast to the shared queue.
   * @param message  Visible text. The hook is purely a message carrier;
   *                  rendering lives in <Toast>.
   * @returns The new toast's id so callers can dismiss it later.
   */
  show: (message: string, type?: ToastType, options?: ToastOptions) => string
  /** Remove a toast by id. No-op if the id is unknown. */
  dismiss: (id: string) => void
  /** Clear the queue. Primarily used by tests and by `dismissAll` flows. */
  dismissAll: () => void
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  show(message, type = 'success', options = {}) {
    const id = nextId()
    const toast: Toast = { id, message, type, action: options.action }
    set(state => {
      const next = [...state.toasts, toast]
      return { toasts: next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next }
    })
    return id
  },

  dismiss(id) {
    set(state => ({ toasts: state.toasts.filter(t => t.id !== id) }))
  },

  dismissAll() {
    if (get().toasts.length === 0) return
    set({ toasts: [] })
  },
}))
